<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useChatStore } from "@/stores/chat";

// Manage AI workers (first-class chat participants). Create them here, then add
// them to channels from the channel header.
const { t } = useI18n();
const chat = useChatStore();

const name = ref("");
const preset = ref("");
const zone = ref("");
const memoryScope = ref("worker");
const loading = ref(true);

async function create() {
  if (!name.value.trim()) return;
  await chat.createWorker(
    name.value.trim(),
    preset.value || undefined,
    zone.value || undefined,
    memoryScope.value,
  );
  name.value = "";
  preset.value = "";
  zone.value = "";
  memoryScope.value = "worker";
}

onMounted(async () => {
  try {
    await chat.loadWorkers();
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="admin">
    <h1>{{ t("workers.title") }}</h1>
    <form class="admin-card workers__new" @submit.prevent="create">
      <input v-model="name" :placeholder="t('workers.name')" data-test="worker-name" />
      <input
        v-model="preset"
        :placeholder="t('workers.preset')"
        :title="t('workers.preset_hint')"
        data-test="worker-preset"
      />
      <input v-model="zone" :placeholder="t('workers.zone')" />
      <label class="workers__scope">
        <span>{{ t("workers.memory_scope") }}</span>
        <select v-model="memoryScope" data-test="worker-memory-scope">
          <option value="worker">{{ t("workers.memory_worker") }}</option>
          <option value="channel">{{ t("workers.memory_channel") }}</option>
        </select>
      </label>
      <button type="submit" class="btn-primary" data-test="worker-create">
        {{ t("workers.create") }}
      </button>
      <p class="workers__hint">{{ t("workers.memory_hint") }}</p>
    </form>

    <ul class="admin-card workers__list">
      <li v-for="w in chat.workers" :key="w.id" :data-test="`worker-row-${w.id}`">
        <strong>{{ w.name }}</strong>
        <span v-if="w.zone" class="workers__meta"> · {{ w.zone }}</span>
        <span v-if="w.preset" class="workers__meta"> · {{ w.preset }}</span>
        <span v-if="w.memory_scope === 'channel'" class="workers__chip" :data-test="`worker-mem-${w.id}`">
          {{ t("workers.memory_chip_channel") }}
        </span>
        <span
          class="workers__runstate"
          :data-run="w.desired_state === 'running' ? 'running' : 'stopped'"
          :data-test="`worker-run-${w.id}`"
        >
          {{ w.desired_state === "running" ? t("workers.running") : t("workers.stopped") }}
        </span>
        <button
          type="button"
          class="workers__life"
          :data-test="
            w.desired_state === 'running' ? `worker-stop-${w.id}` : `worker-start-${w.id}`
          "
          @click="
            chat.setWorkerLifecycle(w.id, w.desired_state === 'running' ? 'stopped' : 'running')
          "
        >
          {{ w.desired_state === "running" ? t("workers.stop") : t("workers.start") }}
        </button>
        <button
          type="button"
          class="workers__del"
          :data-test="`worker-del-${w.id}`"
          :title="t('workers.delete')"
          :aria-label="t('workers.delete')"
          @click="chat.deleteWorker(w.id)"
        >
          ✕
        </button>
      </li>
      <li v-if="loading" class="workers__empty" role="status">
        <span class="spinner" aria-hidden="true"></span> {{ t("admin.loading") }}
      </li>
      <li v-else-if="!chat.workers.length" class="workers__empty">{{ t("workers.empty") }}</li>
    </ul>
    <p v-if="chat.error" class="admin-error">{{ chat.error }}</p>
  </div>
</template>

<style scoped>
.workers__new {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.workers__new input {
  flex: 1;
  min-width: 8rem;
}
.workers__list li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0;
}
.workers__meta {
  opacity: 0.7;
}
.workers__scope {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 14rem;
}
.workers__scope select {
  flex: 1;
}
.workers__hint {
  flex-basis: 100%;
  margin: 0.2rem 0 0;
  font-size: 0.85em;
  opacity: 0.65;
}
.workers__chip {
  font-size: 0.75em;
  padding: 0.05rem 0.4rem;
  border: 1px solid currentColor;
  border-radius: 0.6rem;
  opacity: 0.6;
}
.workers__runstate {
  margin-left: auto;
  font-size: 0.72em;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.workers__runstate[data-run="running"] {
  color: var(--color-ok);
}
.workers__runstate[data-run="stopped"] {
  color: var(--color-fg-dim);
}
.workers__life {
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.1rem 0.55rem;
  font-size: 0.8em;
  color: inherit;
  cursor: pointer;
}
.workers__life:hover {
  border-color: var(--color-accent);
}
.workers__del {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.5;
  cursor: pointer;
}
.workers__del:hover {
  opacity: 1;
}
.workers__empty {
  opacity: 0.6;
}
</style>
